/**
 * PATH: packages/email-hub/src/feature/components/smart-action-bar/BillConfirmReject.tsx
 * FILE_ID: EMAILHUB-SMARTACTIONBAR-BILLCONFIRM-001
 * PURPOSE: The financial-in primary — a "Bill" badge with a ✓ (confirm →
 *          file to Bills + AR/AP) and a ✗ (not a bill → re-classify away).
 * WHY:     Operator (2026-06-03): "it should be automatically classified as
 *          a bill with tick and cross." Replaces a manual "Send to Bills"
 *          button so the disposition reads as the AI's call the operator
 *          simply confirms or overrides — mirrors the inbox list rail's
 *          AI ✓/✗ for job/board suggestions.
 * ROLE IN ARCHITECTURE: Presentational leaf below the SmartActionBar frame.
 * MAIN EXPORTS: BillConfirmReject.
 * NON-RESPONSIBILITIES: No mutation calls — confirm/reject/view handlers are
 *   supplied by the frame, wired to useSmartActionBarRuntime.
 */
import { Check, Loader2, Receipt, X } from 'lucide-react'

import { cn } from '../../../_internal/lib/utils'

export function BillConfirmReject({
  busyConfirm,
  busyReject,
  disabled,
  onConfirm,
  onReject,
  onViewBill,
}: {
  busyConfirm: boolean
  busyReject: boolean
  disabled: boolean
  onConfirm: () => void
  onReject: () => void
  /** When provided, the "Bill" badge becomes a button that opens the
   *  source invoice in the right drawer — a pure view, no mutation. */
  onViewBill?: () => void
}) {
  const anyBusy = busyConfirm || busyReject
  const billBadgeClass =
    'inline-flex items-center gap-1 rounded-full border border-emerald-600/30 bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
  return (
    <div
      className="inline-flex items-center gap-1"
      data-testid="smart-action-bill-confirm"
    >
      {onViewBill ? (
        <button
          type="button"
          onClick={onViewBill}
          title="View the invoice"
          aria-label="View the invoice"
          data-testid="smart-action-bill-view"
          className={cn(billBadgeClass, 'transition-colors hover:bg-emerald-100 dark:hover:bg-emerald-900/40')}
        >
          <Receipt className="h-3 w-3 shrink-0" aria-hidden />
          <span>Bill</span>
        </button>
      ) : (
        <span className={billBadgeClass}>
          <Receipt className="h-3 w-3 shrink-0" aria-hidden />
          <span>Bill</span>
        </span>
      )}
      <button
        type="button"
        onClick={onConfirm}
        disabled={anyBusy || disabled}
        title="Confirm - file this to Bills (AR/AP)"
        aria-label="Confirm bill"
        data-testid="smart-action-bill-confirm-yes"
        className={cn(
          'inline-flex items-center justify-center h-6 w-6 rounded-full border shadow-sm transition-colors',
          'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700',
          (anyBusy || disabled) && 'opacity-60 cursor-not-allowed',
        )}
      >
        {busyConfirm ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        ) : (
          <Check className="h-3.5 w-3.5" aria-hidden />
        )}
      </button>
      <button
        type="button"
        onClick={onReject}
        disabled={anyBusy || disabled}
        title="Not a bill - keep out of Bills"
        aria-label="Not a bill"
        data-testid="smart-action-bill-confirm-no"
        className={cn(
          'inline-flex items-center justify-center h-6 w-6 rounded-full border bg-card shadow-sm transition-colors',
          'border-red-500/40 text-red-500 hover:bg-red-500/15 dark:text-red-400',
          (anyBusy || disabled) && 'opacity-60 cursor-not-allowed',
        )}
      >
        {busyReject ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        ) : (
          <X className="h-3.5 w-3.5" aria-hidden />
        )}
      </button>
    </div>
  )
}
